import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, X, MessageCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { playSparkSound } from '../lib/sparkSound';

const Sparks = () => {
  const { user, profile } = useAuth();
  const [sparks, setSparks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);

  const isInvestor = profile?.role === 'investor';

  const load = async () => {
    if (!user) return;
    const filter = isInvestor ? 'investor_id' : 'entrepreneur_id';
    const { data } = await supabase
      .from('sparks')
      .select(`*, business:businesses(*), investor:profiles!sparks_investor_id_fkey(id, full_name, avatar_url), entrepreneur:profiles!sparks_entrepreneur_id_fkey(id, full_name, avatar_url)`)
      .eq(filter, user.id)
      .order('created_at', { ascending: false });
    setSparks(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    if (!user) return;
    const ch = supabase
      .channel(`sparks-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sparks' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line
  }, [user]);

  const respond = async (sparkId, status) => {
    await supabase.from('sparks').update({ status, responded_at: new Date().toISOString() }).eq('id', sparkId);
    if (status === 'accepted') {
      setShowCelebration(true);
      playSparkSound().catch(() => {});
      setTimeout(() => setShowCelebration(false), 3500);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-6">
        <h1 className="heading-display text-2xl font-bold">Minhas Sparks</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          {isInvestor ? 'As ideias em que você demonstrou interesse.' : 'Investidores que querem conversar com você.'}
        </p>
      </div>

      {loading && <div className="py-12 text-center text-slate-400 text-sm">Carregando...</div>}

      {!loading && sparks.length === 0 && (
        <div className="glass-card rounded-3xl p-10 text-center">
          <Sparkles size={32} className="mx-auto text-[#ffd54f] mb-4" />
          <div className="text-base font-bold">Nenhuma Spark por aqui ainda</div>
          <p className="text-xs text-slate-400 mt-2">
            {isInvestor ? 'Explore o feed e clique em "Tenho Interesse" para acender uma.' : 'Aguarde — investidores estão descobrindo o seu negócio.'}
          </p>
          <Link to="/app/feed" className="inline-flex mt-5 btn-primary rounded-xl px-5 py-3 text-xs font-bold">Ir para o Feed</Link>
        </div>
      )}

      <div className="space-y-3">
        {sparks.map((s) => {
          const other = isInvestor ? s.entrepreneur : s.investor;
          return (
            <motion.div key={s.id} initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} className="glass-card rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center font-bold text-sm">
                  {other?.avatar_url ? <img src={other.avatar_url} alt="" className="w-full h-full object-cover" /> : (other?.full_name || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{other?.full_name}</div>
                  <div className="text-[11px] text-slate-400 truncate">{s.business?.name}</div>
                </div>
                <StatusBadge status={s.status} />
              </div>

              <div className="mt-3 flex items-center gap-2">
                {s.status === 'pending' && !isInvestor && (
                  <>
                    <button onClick={() => respond(s.id, 'accepted')} className="flex-1 btn-gold rounded-xl py-2.5 text-xs font-bold flex items-center justify-center gap-1.5">
                      <Sparkles size={13}/> Aceitar Spark
                    </button>
                    <button onClick={() => respond(s.id, 'rejected')} className="flex-1 btn-secondary rounded-xl py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5">
                      <X size={13}/> Recusar
                    </button>
                  </>
                )}
                {s.status === 'pending' && isInvestor && (
                  <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><Clock size={12}/> Aguardando resposta...</div>
                )}
                {s.status === 'accepted' && (
                  <Link to={`/app/chat/${s.id}`} className="flex-1 btn-primary rounded-xl py-2.5 text-xs font-bold flex items-center justify-center gap-1.5">
                    <MessageCircle size={13}/> Abrir conversa
                  </Link>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {showCelebration && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div initial={{scale:0.7}} animate={{scale:1}} className="relative glass-card rounded-3xl p-8 text-center">
              <Sparkles size={48} className="mx-auto text-[#ffd54f] mb-3" />
              <div className="heading-display text-3xl font-bold gradient-text">⚡ DEU SPARK ⚡</div>
              <p className="mt-2 text-sm text-slate-300">Uma nova oportunidade começou!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { txt: 'Pendente', cls: 'bg-yellow-400/15 text-yellow-300 border-yellow-400/30' },
    accepted: { txt: '⚡ Aceita', cls: 'bg-green-400/15 text-green-300 border-green-400/30' },
    rejected: { txt: 'Recusada', cls: 'bg-red-400/10 text-red-300 border-red-400/20' },
  };
  const s = map[status] || map.pending;
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${s.cls}`}>{s.txt}</span>;
};

export default Sparks;
